/**
 * The two compliance states every Mandatory Compliance surface speaks in — the
 * donut, the by-group bars, the status filter, the table badge and the Excel
 * export all read from here so a person can never be green on one surface and
 * amber on another.
 *
 * Two states, not three (a middle "In progress" band reads as
 * something to interpret — either you met the target or you have not). How far
 * a person got is still on screen: the table shows hours, % of target and the
 * gap.
 *
 * Only INTERNAL MANDATORY hours decide the state: the
 * non-mandatory and External CPD buckets are reference figures and never offset
 * the target.
 *
 * Colours: emerald / amber. Amber rather than red (red is too
 * harsh for a figure people look at all year) and the pair reads apart under
 * colour-vision deficiency, where the red version did not (ΔE 5.8 deutan — a
 * fail). Colour never carries the meaning alone: every state ships with its
 * label and a headcount, which is also what relieves the amber fill's
 * low contrast against a white surface.
 */
export const BANDS = [
  {
    key: 'met',
    label: 'Met target',
    short: 'Met',
    hex: '#059669',
    dot: 'bg-emerald-600',
    bar: 'bg-emerald-600',
    text: 'text-emerald-700',
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  {
    key: 'notMet',
    label: 'Not met yet',
    short: 'Not met',
    // Fill is a step lighter than the ink: a period where nobody has met the
    // target yet is otherwise a wall of dark bars shouting at the reader.
    hex: '#f59e0b',
    dot: 'bg-amber-500',
    bar: 'bg-amber-500',
    text: 'text-amber-700',
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
  },
];

export const BAND_BY_KEY = Object.fromEntries(BANDS.map((b) => [b.key, b]));

/** Which state a staff member is in for the given target. */
export const bandOf = (mandatoryHours, target) =>
  (target > 0 && mandatoryHours >= target ? 'met' : 'notMet');

/** Progress towards the target as a percentage, capped at 100 for display. */
export const progressPct = (mandatoryHours, target) =>
  (target > 0 ? Math.min(100, (mandatoryHours / target) * 100) : 0);

/**
 * State counts + compliance rate for a set of rows already carrying `.band`.
 * `rate` is the share that MET the target — the one number this tab exists to
 * answer, and the number every chart here plots.
 */
export const summarise = (rows) => {
  const counts = { met: 0, notMet: 0 };
  let hours = 0;
  rows.forEach((r) => { counts[r.band] += 1; hours += r.mandatoryHours; });
  const total = rows.length;
  return {
    total,
    counts,
    rate: total > 0 ? (counts.met / total) * 100 : 0,
    avgHours: total > 0 ? hours / total : 0,
  };
};
